// client/src/components/ClientDocs.js

import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { AuthContext } from '../../AuthContext';
import { firestore, storage } from '../../firebase';
import {
  doc,
  collection,
  collectionGroup,
  getDocs,
  onSnapshot,
  updateDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  limit as fsLimit,
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  EmailAuthProvider,
  linkWithCredential
} from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata, uploadBytes } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import { useDataContext } from '../../DataContext';
import { sanitize } from '../../utils/sanitize';
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import { FaUpload } from 'react-icons/fa';
import { listSharedForUserByCompany } from '../../utils/shareStore';
import LegalAccordion from '../shared/LegalAccordion';

// --- Modales "Créer mon compte" (cooldown partagé)
const MODAL_COOLDOWN_MS = 30 * 60 * 1000;
const MODAL_KEY = 'createAccountModalClosedAt';

// --- Limite de scan (fallback legacy) pour maîtriser les coûts si cache vide
const LEGACY_SCAN_LIMIT = 30;
const FIDELITY_CACHE_KEY_PREFIX = 'fid_cards_';
const FIDELITY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

// --- TTL cache logos
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// --- Délai max avant d'afficher la fidélité (fallback sécurité)
const INITIAL_FIDELITY_READY_FALLBACK_MS = 2000;

// --- Cache photos (hybride SWR)
const PHOTOS_CACHE_KEY = 'userPhotos';
const PHOTOS_LASTSYNC_KEY = 'userPhotos_lastSync';
const PHOTOS_TTL_MS = 5 * 60 * 1000; // 5 min

// --- Entreprises (même logique que ClientSettings pour détecter une affiliation)
const INV_SCAN_TTL_MS = 15 * 60 * 1000; // 15 min
// Firestore rules only allow list queries when limit <= 50 (see firestore.rules)
const INV_LIMIT_SCAN = 50;
const INV_CACHE_KEY = (email) => `inv_cache_${email}`;
const INV_KNOWN_COMPANIES_KEY = (email) => `inv_known_companies_${email}`;
const SETTINGS_CACHE_KEY = 'clientSettings';

// --- Helpers cache (reprise de ClientSettings)
const fromCache = (key, ttl) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    if (raw.startsWith('{') || raw.startsWith('[')) {
      const obj = JSON.parse(raw);
      if (ttl == null) return obj;
      if (!obj || typeof obj !== 'object' || typeof obj.ts !== 'number') return null;
      if (Date.now() - obj.ts > ttl) return null;
      return obj;
    }
    if (ttl == null) return raw;
    return raw;
  } catch {
    return null;
  }
};

const putCache = (key, payload) => {
  try {
    const value = typeof payload === 'string' ? payload : JSON.stringify({ ...payload, ts: Date.now() });
    localStorage.setItem(key, value);
  } catch {}
};

// --- Helper: tableau unique trié (stable pour comparer les indexes)
const uniqSorted = (arr = []) =>
  Array.from(new Set((Array.isArray(arr) ? arr : []).filter(Boolean))).sort();

// =======================================================
// Composant Logo marchand (cache localStorage + TTL)
// =======================================================
const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;

    const cacheKey = `merchantLogo_${merchantId}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (parsed?.url) setLogoUrl(parsed.url);
        if (parsed?.timestamp && Date.now() - parsed.timestamp < LOGO_TTL_MS) {
          return; // cache encore frais → pas d'appel Storage
        }
      } catch {}
    }

    let mounted = true;
    (async () => {
      try {
        const logoRef = ref(storage, `${merchantId}/photo-profil/`);
        const logoList = await listAll(logoRef);

        if (logoList.items.length > 0) {
          const logosWithMetadata = await Promise.all(
            logoList.items.map(async (item) => {
              const metadata = await getMetadata(item);
              const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(0);
              return { item, timeCreated };
            })
          );
          logosWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
          const latestLogo = logosWithMetadata[0];

          const freshUrl = await getDownloadURL(latestLogo.item);
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ url: freshUrl, timestamp: Date.now(), latest: latestLogo.timeCreated.getTime() })
          );
          if (mounted) setLogoUrl(freshUrl);
        } else {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
        }
      } catch {
        if (mounted) setLogoUrl(null);
      }
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      loading="lazy"
      src={logoUrl}
      alt="Logo du commerçant"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 'inherit'
      }}
    />
  ) : null;
};

// =======================================================
// Helpers tri fidélité
// =======================================================
const sortLoyaltyData = (data, isRewardImminentFn) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const withFlags = data.map((item) => ({
    ...item,
    isRewardImminent: isRewardImminentFn(item),
  }));

  const byRecentActivity = (a, b) => (b.lastActivity || 0) - (a.lastActivity || 0);
  const imminent = withFlags.filter((item) => item.isRewardImminent).sort(byRecentActivity);
  const others = withFlags.filter((item) => !item.isRewardImminent).sort(byRecentActivity);

  return imminent.length > 0 ? imminent.concat(others) : others;
};

const ClientDocs = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line
  const { fetchData, dataCache } = useDataContext();
  const { reloadEmailVerified } = useContext(AuthContext);

  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState(() => {
    const stored = localStorage.getItem(PHOTOS_CACHE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [showCGVModal, setShowCGVModal] = useState(false);
  const [acceptedCGV, setAcceptedCGV] = useState(false);
  const [fidelityData, setFidelityData] = useState([]);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const [isFidelityReady, setIsFidelityReady] = useState(false);
  const [isCompanyLinked, setIsCompanyLinked] = useState(null);

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAccountPseudo, setNewAccountPseudo] = useState('');
  const [acceptedCGVCreate, setAcceptedCGVCreate] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // --- caches & structures ---
  const merchantMetaCache = useRef({});
  const pendingMetaFetch = useRef(new Set());
  const cardsMap = useRef({});
  const legacyUnsubs = useRef({});
  const attachedMerchants = useRef(new Set());
  const newSchemaUnsubs = useRef({});
  const currentUidRef = useRef(null);
  const companyUnsubsRef = useRef({});
  const companyAcceptedRef = useRef({});
  const companyScanLockRef = useRef(false);
  const lastCompanyEmailRef = useRef('');
  const remoteIndexRef = useRef(null);
  const remoteIndexKeyRef = useRef('');
  const lastPersistedIndexKeyRef = useRef('');
  const indexPersistTimerRef = useRef(null);

  // --- UI refs ---
  const fileInputRef = useRef(null);
  const importBusyRef = useRef(false);

  // --- Storage sync guard
  const syncingRef = useRef(false);
  const fidelityInitialLoad = useRef({ pending: new Set(), scanComplete: false, timer: null });
  const fidelityReadyRef = useRef(false);

  useEffect(() => {
    fidelityReadyRef.current = isFidelityReady;
  }, [isFidelityReady]);

  useEffect(() => {
    const tracker = fidelityInitialLoad.current;
    return () => {
      if (tracker.timer) {
        clearTimeout(tracker.timer);
        tracker.timer = null;
      }
    };
  }, []);

  const resetInitialFidelityTracker = useCallback(() => {
    const tracker = fidelityInitialLoad.current;
    tracker.pending = new Set();
    tracker.scanComplete = false;
    if (tracker.timer) {
      clearTimeout(tracker.timer);
      tracker.timer = null;
    }
    setIsFidelityReady(false);
  }, []);

  const registerPendingMerchant = useCallback((merchantId) => {
    if (!merchantId || fidelityReadyRef.current) return;
    const tracker = fidelityInitialLoad.current;
    if (tracker.pending.has(merchantId)) return;
    tracker.pending.add(merchantId);
  }, []);

  const resolvePendingMerchant = useCallback((merchantId) => {
    if (!merchantId) return;
    const tracker = fidelityInitialLoad.current;
    if (!tracker.pending.has(merchantId)) return;
    tracker.pending.delete(merchantId);
    if (tracker.scanComplete && tracker.pending.size === 0) {
      if (tracker.timer) {
        clearTimeout(tracker.timer);
        tracker.timer = null;
      }
      setIsFidelityReady(true);
    }
  }, []);

  const saveFidelityCache = useCallback((uid) => {
    if (!uid) return;
    try {
      const snapshot = Object.values(cardsMap.current);
      const payload = { ts: Date.now(), data: snapshot };
      localStorage.setItem(`${FIDELITY_CACHE_KEY_PREFIX}${uid}`, JSON.stringify(payload));
    } catch {}
  }, []);

  const loadFidelityCache = useCallback((uid) => {
    if (!uid) return [];
    try {
      const raw = localStorage.getItem(`${FIDELITY_CACHE_KEY_PREFIX}${uid}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return [];
      if (parsed.ts && Date.now() - parsed.ts > FIDELITY_CACHE_TTL_MS) return [];
      return Array.isArray(parsed.data) ? parsed.data : [];
    } catch {
      return [];
    }
  }, []);

  const clearFidelityCache = useCallback((uid) => {
    if (!uid) return;
    localStorage.removeItem(`${FIDELITY_CACHE_KEY_PREFIX}${uid}`);
  }, []);

  const commitCards = useCallback(() => {
    const snapshot = Object.values(cardsMap.current);
    setFidelityData(snapshot);
    const uid = currentUidRef.current;
    if (uid) saveFidelityCache(uid);
  }, [saveFidelityCache]);

  const completeInitialFidelityScan = useCallback(() => {
    const tracker = fidelityInitialLoad.current;
    tracker.scanComplete = true;
    if (tracker.pending.size === 0) {
      if (tracker.timer) {
        clearTimeout(tracker.timer);
        tracker.timer = null;
      }
      setIsFidelityReady(true);
      return;
    }
    if (!tracker.timer) {
      tracker.timer = setTimeout(() => {
        const currentTracker = fidelityInitialLoad.current;
        currentTracker.timer = null;
        setIsFidelityReady(true);
      }, INITIAL_FIDELITY_READY_FALLBACK_MS);
    }
  }, []);

  // --- Entreprise: détection d'affiliation (même logique que ClientSettings)
  const stopCompanyListeners = useCallback(() => {
    Object.values(companyUnsubsRef.current).forEach((fn) => fn && fn());
    companyUnsubsRef.current = {};
    companyAcceptedRef.current = {};
  }, []);

  const recomputeCompanyLink = useCallback(() => {
    const has = Object.values(companyAcceptedRef.current).some(Boolean);
    setIsCompanyLinked(has);
  }, []);

  const attachCompanyListener = useCallback((companyId, email) => {
    if (!companyId || !email || companyUnsubsRef.current[companyId]) return;

    const compRef = doc(firestore, 'entreprises', companyId);
    companyUnsubsRef.current[companyId] = onSnapshot(
      compRef,
      (snap) => {
        const data = snap.data() || {};
        const invitations = Array.isArray(data.invitations) ? data.invitations : [];
        const accepted = invitations.some(
          (inv) => inv?.employeeEmail === email && inv?.status === 'accepted'
        );
        companyAcceptedRef.current[companyId] = accepted;
        recomputeCompanyLink();
      },
      (err) => console.error('company listener error:', err)
    );
  }, [recomputeCompanyLink]);

  const discoverCompanyLink = useCallback(async (email) => {
    if (!email) {
      stopCompanyListeners();
      setIsCompanyLinked(false);
      lastCompanyEmailRef.current = '';
      return;
    }

    if (companyScanLockRef.current) {
      if (lastCompanyEmailRef.current === email) return;
      companyScanLockRef.current = false;
    }

    stopCompanyListeners();
    lastCompanyEmailRef.current = email;

    const cachedSettings = fromCache(SETTINGS_CACHE_KEY, null);
    if (cachedSettings?.company?.id) {
      companyAcceptedRef.current[cachedSettings.company.id] = true;
      recomputeCompanyLink();
    }

    const cached = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS);
    if (cached?.companies?.length) {
      if (typeof cached.hasCompany === 'boolean') {
        setIsCompanyLinked(cached.hasCompany);
      }
      cached.companies.forEach((id) => attachCompanyListener(id, email));
      if (!navigator.onLine || cached.hasCompany === true) return;
    }

    if (companyScanLockRef.current) return;
    companyScanLockRef.current = true;

    try {
      const entreprisesRef = collection(firestore, 'entreprises');
      const snap = await getDocs(query(entreprisesRef, fsLimit(INV_LIMIT_SCAN)));
      const pendingItems = [];
      const companies = new Set();
      let hasAccepted = false;

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const arr = Array.isArray(data.invitations) ? data.invitations : [];
        const pendings = arr
          .filter((inv) => inv?.employeeEmail === email && inv?.status === 'pending')
          .map((inv) => ({ id: docSnap.id, ...inv }));
        pendings.forEach((p) => pendingItems.push(p));

        const accepted = arr.some((inv) => inv?.employeeEmail === email && inv?.status === 'accepted');
        companyAcceptedRef.current[docSnap.id] = accepted;
        if (accepted) hasAccepted = true;
        if (accepted || pendings.length) companies.add(docSnap.id);
      });

      setIsCompanyLinked(hasAccepted);
      const out = { items: pendingItems, companies: Array.from(companies), hasCompany: hasAccepted };
      putCache(INV_CACHE_KEY(email), out);
      putCache(INV_KNOWN_COMPANIES_KEY(email), { companies: out.companies });
      out.companies.forEach((id) => attachCompanyListener(id, email));
    } catch (err) {
      console.error('company link scan error:', err);
      setIsCompanyLinked((prev) => (prev === null ? false : prev));
    } finally {
      companyScanLockRef.current = false;
    }
  }, [attachCompanyListener, recomputeCompanyLink, stopCompanyListeners]);

  const normalizePhotoEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const takenByUid = typeof entry.takenByUid === 'string' ? entry.takenByUid : '';
    const rawPseudo = typeof entry.takenByPseudo === 'string' ? entry.takenByPseudo : '';
    const takenByPseudo = rawPseudo ? sanitize(rawPseudo).trim() : '';
    const rawCategory = typeof entry.category === 'string' ? entry.category : '';
    const safeCategory = sanitize(rawCategory || '').trim();
    const category = safeCategory || 'perso';
    return {
      ...entry,
      takenByUid,
      takenByPseudo,
      category,
    };
  };

  // --- styles 2025 premium (Apple/Spotify/Netflix inspired)
  const pageStyles = `
  .dashboard-container.client-docs-dashboard{
    align-items:stretch;
  }

  .client-docs-shell{
    flex:1;
    display:flex;
    flex-direction:column;
    width:100%;
  }

  .client-docs-inner{
    width:100%;
    max-width:var(--eb-content-max);
    margin:0 auto;
    display:flex;
    flex-direction:column;
    gap:28px;
  }

  /* EN-TÊTE */
  .client-docs-hero{ width:100%; }

  .client-docs-heroGrid{
    display:grid;
    gap:20px;
    align-items:end;
  }

  @media (min-width:960px){
    .client-docs-heroGrid{ grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:32px; }
  }

  .client-docs-heroIntro{ display:flex; flex-direction:column; gap:4px; min-width:0; }

  .client-docs-heroIntro h1{
    margin:0;
    text-align:left;
    font-size:26px;
    font-weight:700;
    line-height:1.2;
    letter-spacing:-0.02em;
    color:var(--eb-text);
    overflow-wrap:anywhere;
  }

  .client-docs-heroIntro p{
    margin:0;
    font-size:14.5px;
    line-height:1.5;
    color:var(--eb-muted);
  }

  .client-docs-heroStats{
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(130px,1fr));
    gap:12px;
  }

  .client-docs-heroStat{
    padding:16px 18px;
    border-radius:var(--eb-radius-lg);
    background:var(--eb-surface);
    border:1px solid var(--eb-border);
    box-shadow:var(--eb-shadow-sm);
    display:flex;
    flex-direction:column;
    gap:4px;
    min-width:0;
  }

  .client-docs-statValue{
    font-size:26px;
    font-weight:700;
    line-height:1.1;
    letter-spacing:-0.02em;
    color:var(--eb-text);
    font-variant-numeric:tabular-nums;
  }

  .client-docs-statLabel{
    font-size:13px;
    line-height:1.3;
    color:var(--eb-muted);
    font-weight:500;
  }

  /* CONTENU */
  .client-docs-stage{
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr));
    gap:20px;
    align-items:stretch;
  }

  .client-docs-panel{
    position:relative;
    min-width:0;
    background:var(--eb-surface);
    border-radius:var(--eb-radius-lg);
    padding:24px;
    border:1px solid var(--eb-border);
    box-shadow:var(--eb-shadow-sm);
    display:flex;
    flex-direction:column;
    gap:20px;
    transition:border-color .15s ease, box-shadow .15s ease;
  }

  .client-docs-panel--clickable{ cursor:pointer; }

  .client-docs-panel--clickable:hover{
    border-color:var(--eb-border-strong);
    box-shadow:var(--eb-shadow-md);
  }

  .client-docs-panel:focus-visible{ outline:none; box-shadow:var(--eb-ring); }

  .client-docs-panelHeader{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
  }

  .client-docs-panelTitle{ display:flex; flex-direction:column; gap:2px; min-width:0; }

  .client-docs-panelTitle span{
    font-size:12.5px;
    font-weight:600;
    letter-spacing:0.06em;
    text-transform:uppercase;
    color:var(--eb-subtle);
  }

  .client-docs-panelTitle h2{
    margin:0;
    font-size:20px;
    font-weight:700;
    color:var(--eb-text);
    letter-spacing:-0.015em;
  }

  .client-docs-iconButton{
    width:40px;
    height:40px;
    border-radius:var(--eb-radius-sm);
    border:1px solid var(--eb-border-strong);
    background:var(--eb-surface);
    color:var(--eb-brand);
    display:flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
    flex-shrink:0;
  }
  .client-docs-iconButton svg{ width:18px; height:18px; }
  .client-docs-iconButton:hover{ background:var(--eb-surface-2); }

  /* Bouton secondaire en haut à droite du panneau */
  .client-docs-actionPill{
    flex-shrink:0;
    min-height:36px;
    padding:0 14px;
    border-radius:var(--eb-radius-sm);
    border:1px solid var(--eb-border-strong);
    background:var(--eb-surface);
    color:var(--eb-text-2);
    box-shadow:var(--eb-shadow-xs);
    font-weight:600;
    font-size:13.5px;
    line-height:1;
    cursor:pointer;
    white-space:nowrap;
    display:inline-flex;
    align-items:center;
    gap:8px;
    transition:background-color .15s ease, color .15s ease;
  }
  .client-docs-actionPill svg{ color:var(--eb-brand); }
  .client-docs-actionPill:hover{ background:var(--eb-surface-2); color:var(--eb-text); }
  .client-docs-actionPill:focus-visible{ outline:none; box-shadow:var(--eb-ring); }

  .client-docs-panelBody{
    display:flex;
    flex-direction:column;
    gap:12px;
    min-width:0;
  }

  .client-docs-panelBody--rail{
    margin:0 -24px;
    padding:0 24px;
    overflow:hidden;
  }

  .client-docs-railTrack{
    display:flex;
    gap:12px;
    overflow-x:auto;
    padding:2px 2px 10px;
    scroll-snap-type:x mandatory;
    -webkit-overflow-scrolling:touch;
    scrollbar-width:thin;
    scrollbar-color:var(--eb-border-strong) transparent;
  }
  .client-docs-railTrack::-webkit-scrollbar{ height:6px; }
  .client-docs-railTrack::-webkit-scrollbar-track{ background:transparent; }
  .client-docs-railTrack::-webkit-scrollbar-thumb{ background:var(--eb-border-strong); border-radius:3px; }

  .client-docs-railTrack--triangle{ display:flex; }
  .client-docs-loyaltyCard--triangleBottom{ align-self:stretch; }

  /* CARTES FIDÉLITÉ */
  .client-docs-loyaltyCard{
    flex:0 0 auto;
    min-width:250px;
    max-width:320px;
    scroll-snap-align:start;
    border-radius:var(--eb-radius);
    padding:14px 16px;
    border:1px solid var(--eb-border);
    background:var(--eb-surface);
    display:flex;
    gap:14px;
    align-items:center;
    text-align:left;
    cursor:pointer;
    color:var(--eb-text);
    transition:border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
  }
  .client-docs-loyaltyCard:hover{ border-color:var(--eb-brand-300); box-shadow:var(--eb-shadow-md); }
  .client-docs-loyaltyCard:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
  .client-docs-loyaltyCard--imminent{ background:var(--eb-warning-bg); border-color:#fde3b0; }
  .client-docs-loyaltyCard--imminent:hover{ border-color:#f5c98a; }

  .client-docs-loyaltyAvatar{
    width:52px;
    height:52px;
    border-radius:50%;
    background:var(--eb-brand-50);
    border:1px solid var(--eb-border);
    display:flex;
    align-items:center;
    justify-content:center;
    overflow:hidden;
    flex-shrink:0;
    color:var(--eb-brand);
    font-weight:700;
    font-size:19px;
    text-transform:uppercase;
  }
  .client-docs-loyaltyAvatar:empty::after{ content:attr(data-initial); }

  .client-docs-loyaltyInfo{ display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }

  .client-docs-loyaltyInfo h3{
    margin:0;
    text-align:left;
    font-size:15px;
    font-weight:600;
    color:var(--eb-text);
    letter-spacing:-0.01em;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }

  .client-docs-loyaltyMeta{ display:flex; flex-direction:column; gap:2px; }
  .client-docs-loyaltyMeta p{ margin:0; font-size:13.5px; color:var(--eb-muted); font-weight:500; }
  .client-docs-loyaltyMeta .client-docs-imminentText{ color:var(--eb-warning); font-weight:600; }

  .client-docs-loyaltyLoading{
    min-height:120px;
    display:flex;
    flex-direction:column;
    gap:12px;
    align-items:center;
    justify-content:center;
    text-align:center;
    font-size:14px;
    color:var(--eb-muted);
    background:var(--eb-surface-2);
    border-radius:var(--eb-radius);
    border:1px dashed var(--eb-border-strong);
  }

  /* LISTE DOCUMENTS */
  .client-docs-panelBody--stack{ gap:8px; }
  .client-docs-invoiceList{ display:flex; flex-direction:column; gap:8px; }

  .client-docs-invoiceRow{
    display:flex;
    align-items:center;
    gap:14px;
    width:100%;
    padding:10px 12px;
    border-radius:var(--eb-radius);
    border:1px solid var(--eb-border);
    background:var(--eb-surface);
    text-align:left;
    cursor:pointer;
    color:var(--eb-text);
    transition:border-color .15s ease, background-color .15s ease;
  }
  .client-docs-invoiceRow:hover{ background:var(--eb-surface-2); border-color:var(--eb-border-strong); }
  .client-docs-invoiceRow:focus-visible{ outline:none; box-shadow:var(--eb-ring); }

  .client-docs-invoicePreview{
    width:52px;
    height:52px;
    border-radius:var(--eb-radius-sm);
    overflow:hidden;
    flex-shrink:0;
    background:var(--eb-surface-2);
    border:1px solid var(--eb-border);
  }
  .client-docs-invoicePreview img{ width:100%; height:100%; object-fit:cover; display:block; }

  .client-docs-invoiceCopy{ display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }

  .client-docs-invoiceTitle{
    font-size:14.5px;
    font-weight:600;
    color:var(--eb-text);
    line-height:1.3;
    white-space:nowrap;
    text-overflow:ellipsis;
    overflow:hidden;
  }

  .client-docs-invoiceSubtitle{
    font-size:13px;
    color:var(--eb-muted);
    white-space:nowrap;
    text-overflow:ellipsis;
    overflow:hidden;
  }

  .client-docs-invoiceChevron{
    font-size:22px;
    color:var(--eb-subtle);
    line-height:1;
    transition:transform .15s ease, color .15s ease;
  }
  .client-docs-invoiceRow:hover .client-docs-invoiceChevron{ transform:translateX(2px); color:var(--eb-brand); }

  /* ÉTAT VIDE */
  .client-docs-empty{
    padding:28px 20px;
    border-radius:var(--eb-radius);
    background:var(--eb-surface-2);
    border:1px dashed var(--eb-border-strong);
    color:var(--eb-muted);
    font-size:14px;
    line-height:1.5;
    text-align:center;
  }

  /* PIED DE PANNEAU */
  .client-docs-panelFooter{
    margin-top:auto;
    display:flex;
    justify-content:flex-end;
    gap:12px;
    flex-wrap:wrap;
    align-items:center;
    padding-top:16px;
    border-top:1px solid var(--eb-border);
  }

  .client-docs-cta{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    min-height:40px;
    padding:0 14px;
    border-radius:var(--eb-radius-sm);
    font-size:14px;
    font-weight:600;
    border:1px solid transparent;
    cursor:pointer;
    transition:background-color .15s ease, color .15s ease;
  }
  .client-docs-cta--ghost{ background:transparent; color:var(--eb-brand); margin-right:-14px; }
  .client-docs-cta--ghost::after{ content:'→'; margin-left:8px; transition:transform .15s ease; }
  .client-docs-cta--ghost:hover:not(:disabled){ background:var(--eb-brand-50); }
  .client-docs-cta--ghost:hover:not(:disabled)::after{ transform:translateX(2px); }
  .client-docs-cta:disabled{ opacity:0.45; cursor:not-allowed; }
  .client-docs-cta:focus-visible{ outline:none; box-shadow:var(--eb-ring); }

  @media (min-width:1024px){
    .client-docs-loyaltyCard{ flex:1 1 250px; max-width:none; }
    .client-docs-railTrack--triangle{
      display:grid;
      grid-template-columns:repeat(2,minmax(0,1fr));
      gap:12px;
      overflow:visible;
      padding-bottom:2px;
    }
    .client-docs-loyaltyCard--triangleTop{ min-width:0; max-width:none; width:100%; }
    .client-docs-loyaltyCard--triangleBottom{ grid-column:1 / -1; min-width:0; max-width:none; width:100%; }
  }

  /* MODALES */
  .client-docs-overlay{
    position:fixed;
    inset:0;
    z-index:1000;
    display:flex;
    align-items:center;
    justify-content:center;
    padding:16px;
    background:rgba(15,35,56,.55);
    backdrop-filter:blur(4px);
    -webkit-backdrop-filter:blur(4px);
  }
  .client-docs-modal{
    position:relative;
    width:100%;
    max-width:440px;
    max-height:calc(100vh - 32px);
    max-height:calc(100dvh - 32px);
    overflow-y:auto;
    -webkit-overflow-scrolling:touch;
    background:var(--eb-surface);
    border-radius:var(--eb-radius-xl);
    box-shadow:var(--eb-shadow-lg);
    padding:24px;
    display:flex;
    flex-direction:column;
    gap:14px;
    text-align:left;
  }
  .client-docs-modal h2{
    margin:0 0 4px;
    padding-right:44px;
    text-align:left;
    font-size:22px;
    font-weight:700;
    letter-spacing:-0.02em;
    color:var(--eb-text);
  }
  .client-docs-modal__close{
    position:absolute;
    top:14px;
    right:14px;
    width:40px;
    height:40px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    border:0;
    border-radius:var(--eb-radius-sm);
    background:transparent;
    color:var(--eb-muted);
    font-size:26px;
    line-height:1;
    cursor:pointer;
  }
  .client-docs-modal__close:hover{ background:var(--eb-surface-2); color:var(--eb-text); }
  .client-docs-pwd > div{ margin-bottom:0 !important; }
  .client-docs-pwd > ul{ margin:10px 0 0 !important; }
  .client-docs-pwd .eb-input{ padding-right:44px; }

  /* RESPONSIVE */
  @media (max-width:720px){
    .client-docs-inner{ gap:20px; }
    .client-docs-heroIntro h1{ font-size:22px; }
    .client-docs-heroStats{ grid-template-columns:repeat(auto-fit,minmax(96px,1fr)); gap:8px; }
    .client-docs-heroStat{ padding:12px 14px; border-radius:var(--eb-radius); }
    .client-docs-statValue{ font-size:22px; }
    .client-docs-statLabel{ font-size:12.5px; }
    .client-docs-stage{ gap:16px; }
    .client-docs-panel{ padding:20px 16px; gap:16px; }
    .client-docs-panelBody--rail{ margin:0 -16px; padding:0 16px; }
    .client-docs-railTrack{ scroll-padding-left:16px; }
    .client-docs-railTrack--triangle{ display:flex; overflow-x:auto; padding-bottom:10px; }
    .client-docs-loyaltyCard--triangleBottom{ grid-column:auto; }
    .client-docs-panelFooter{ padding-top:12px; }
  }

  @media (max-width:480px){
    .client-docs-loyaltyCard{ min-width:240px; max-width:82vw; }
    .client-docs-overlay{ align-items:flex-end; padding:0; }
    .client-docs-modal{
      max-width:none;
      max-height:calc(100dvh - 24px);
      border-radius:var(--eb-radius-xl) var(--eb-radius-xl) 0 0;
      padding:22px 20px calc(20px + env(safe-area-inset-bottom, 0px));
    }
  }

  @media (prefers-reduced-motion:reduce){
    .client-docs-panel,
    .client-docs-loyaltyCard,
    .client-docs-invoiceRow,
    .client-docs-cta{ transition:none; }
  }
`;

  // CGU helper
  const hasAcceptedCGU = user?.CGV === 'accepted';

  const missingCGV = !hasAcceptedCGU;
  const isPwdValid = PASSWORD_REGEX.test(newPassword || '');
  const isSaveDisabled = missingCGV && !acceptedCGV;
  const isCGVButtonDisabled = isSaveDisabled;
  const isCreateDisabled =
    isCreating ||
    !newEmail.trim() ||
    !newPassword.trim() ||
    !newAccountPseudo.trim() ||
    (!hasAcceptedCGU && !acceptedCGVCreate) ||
    !isPwdValid;

  // 1) Persistance de session
  useEffect(() => {
    setIsAuthLoaded(true);
  }, []);

  // ---------- PHOTOS: logique hybride SWR (cache + diff) ----------
  const loadCachedPhotos = useCallback(() => {
    try {
      const raw = localStorage.getItem(PHOTOS_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((entry) => normalizePhotoEntry(entry))
        .filter(Boolean);
    } catch { return []; }
  }, []);

  const saveCachedPhotos = useCallback((arr) => {
    try {
      const normalized = Array.isArray(arr)
        ? arr.map((entry) => normalizePhotoEntry(entry)).filter(Boolean)
        : [];
      localStorage.setItem(PHOTOS_CACHE_KEY, JSON.stringify(normalized));
      localStorage.setItem(PHOTOS_LASTSYNC_KEY, String(Date.now()));
    } catch {}
  }, []);

  const needRefresh = (key, ttl) => {
    const raw = localStorage.getItem(key);
    const last = Number(raw || 0);
    return !Number.isFinite(last) || Date.now() - last > ttl;
  };

  const diffSyncPhotos = useCallback(
    async (force = false) => {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid || syncingRef.current) return;

      const cached = loadCachedPhotos();
      if (cached.length && !force) {
        setPhotos(cached.slice().sort((a, b) => b.timestamp - a.timestamp));
      }

      if (!force && !needRefresh(PHOTOS_LASTSYNC_KEY, PHOTOS_TTL_MS) && cached.length) {
        return;
      }

      syncingRef.current = true;
      try {
        const photosRef = ref(storage, `${uid}/photos/`);
        const list = await listAll(photosRef);
        const refsByName = new Map(list.items.map((it) => [it.name, it]));
        const names = new Set(refsByName.keys());

        const cachedMap = Object.fromEntries(cached.map((p) => [p.name, p]));
        const toAdd = list.items.filter((it) => !cachedMap[it.name]);
        const surviving = cached
          .filter((p) => names.has(p.name))
          .map((entry) => normalizePhotoEntry(entry))
          .filter(Boolean);

        const acceptedCompanyUID = user?.acceptedCompanyUID || null;
        const sharedDocs = acceptedCompanyUID
          ? await listSharedForUserByCompany(acceptedCompanyUID, uid)
          : [];
        const sharedMap = new Map();
        sharedDocs.forEach((d) => {
          const fname = d.fileName || d.name || (d.id?.split('__')[1] || '');
          if (!fname) return;
          const compUid = d.companyUid || acceptedCompanyUID || null;
          if (compUid) sharedMap.set(fname, compUid);
        });

        const categoryFor = (name) => sharedMap.get(name) || 'perso';

        const enrichedExisting = await Promise.all(
          surviving.map(async (photo) => {
            const hasAuthor =
              (photo.takenByUid && photo.takenByUid.length) ||
              (photo.takenByPseudo && photo.takenByPseudo.length);
            if (hasAuthor) {
              return normalizePhotoEntry({ ...photo, category: categoryFor(photo.name) });
            }
            const storageItem = refsByName.get(photo.name);
            if (!storageItem) return normalizePhotoEntry({ ...photo, category: categoryFor(photo.name) });
            try {
              const meta = await getMetadata(storageItem);
              const custom = meta.customMetadata || {};
              const rawPseudo = custom.TakenByPseudo || '';
              const takenByUid = custom.TakenByUID || custom.TakenByUid || '';
              const takenByPseudo = rawPseudo ? sanitize(rawPseudo) : '';
              return normalizePhotoEntry({
                ...photo,
                takenByUid: takenByUid || photo.takenByUid || '',
                takenByPseudo: takenByPseudo || photo.takenByPseudo || '',
                category: categoryFor(photo.name),
              });
            } catch {
              return normalizePhotoEntry({ ...photo, category: categoryFor(photo.name) });
            }
          })
        );

        const added = await Promise.all(
          toAdd.map(async (item) => {
            try {
              const [meta, url] = await Promise.all([getMetadata(item), getDownloadURL(item)]);
              const ts = meta.timeCreated ? new Date(meta.timeCreated).getTime() : Date.now();
              const title = meta.customMetadata?.title || 'Aucun titre';
              const custom = meta.customMetadata || {};
              const rawPseudo = custom.TakenByPseudo || '';
              const takenByUid = custom.TakenByUID || custom.TakenByUid || '';
              const takenByPseudo = rawPseudo ? sanitize(rawPseudo) : '';
              return normalizePhotoEntry({
                name: item.name,
                url,
                timestamp: ts,
                title,
                takenByUid,
                takenByPseudo,
                category: categoryFor(item.name),
              });
            } catch { return null; }
          })
        );

        const merged = [...enrichedExisting, ...added.filter(Boolean)]
          .filter((v, i, self) => i === self.findIndex((t) => t.name === v.name))
          .sort((a, b) => b.timestamp - a.timestamp);

        setPhotos(merged);
        saveCachedPhotos(merged);
      } catch (e) {
        console.error('photos sync error:', e);
        if (!cached.length) alert('Impossible de récupérer les photos.');
      } finally {
        syncingRef.current = false;
      }
    },
    [loadCachedPhotos, saveCachedPhotos, user?.acceptedCompanyUID]
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportInvoice = useCallback(
    async (event) => {
      const input = event?.target;
      const file = input?.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image (PNG, JPG, HEIC, etc.)');
        if (input) input.value = null;
        return;
      }

      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) {
        alert('Utilisateur non connecté.');
        if (input) input.value = null;
        return;
      }

      if (importBusyRef.current) {
        if (input) input.value = null;
        return;
      }
      importBusyRef.current = true;

      try {
        const rawName = file.name.replace(/\.[^/.]+$/, '');
        const cleanTitle = sanitize(rawName) || 'Document importé';
        const sanitizedFilename = sanitize(file.name);
        const cleanFilename = sanitizedFilename || file.name;
        const takenByPseudo = sanitize(user?.pseudo || '') || 'Moi';

        const storageRef = ref(storage, `${uid}/photos/${cleanFilename}`);
        await uploadBytes(storageRef, file, {
          customMetadata: {
            TakenByPseudo: takenByPseudo,
            title: cleanTitle,
            category: 'perso',
          },
        });

        const [meta, url] = await Promise.all([getMetadata(storageRef), getDownloadURL(storageRef)]);
        const ts = meta.timeCreated ? new Date(meta.timeCreated).getTime() : Date.now();

        const optimistic = normalizePhotoEntry({
          name: cleanFilename,
          url,
          timestamp: ts,
          title: cleanTitle,
          takenByPseudo,
          takenByUid: uid,
          category: 'perso',
        });

        const current = loadCachedPhotos();
        const merged = [optimistic, ...current.filter((p) => p?.name !== optimistic.name)]
          .filter(Boolean)
          .sort((a, b) => b.timestamp - a.timestamp);

        setPhotos(merged);
        saveCachedPhotos(merged);

        await diffSyncPhotos(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erreur lors de l’import du document :', e);
        alert('Erreur lors de l’import du document.');
      } finally {
        importBusyRef.current = false;
        if (input) input.value = null;
      }
    },
    [diffSyncPhotos, loadCachedPhotos, saveCachedPhotos, user]
  );

  // 2) Auth + user + modales + démarrage sync hybrides
  useEffect(() => {
    if (!isAuthLoaded) return;

    const auth = getAuth();
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/');
        if (unsubscribeUser) {
          unsubscribeUser();
          unsubscribeUser = null;
        }
        stopCompanyListeners();
        setIsCompanyLinked(false);
        lastCompanyEmailRef.current = '';
        companyScanLockRef.current = false;
        setUser(null);
        return;
      }

      const uid = currentUser.uid;
      const userRef = doc(firestore, 'users', uid);

      unsubscribeUser = onSnapshot(userRef, (docSnap) => {
        const uData = docSnap.data() || {};
        const resolvedEmail = uData.email || currentUser.email || '';
        setUser({ ...uData, email: resolvedEmail, uid, isAnonymous: currentUser.isAnonymous });

        const cachedPhotos = loadCachedPhotos();
        if (cachedPhotos.length) setPhotos(cachedPhotos);
        diffSyncPhotos(false);
        discoverCompanyLink(resolvedEmail);

        if (currentUser.isAnonymous) {
          const raw = localStorage.getItem(MODAL_KEY);
          const closedAt = Number.isFinite(parseInt(raw, 10)) ? parseInt(raw, 10) : 0;
          if (Date.now() - closedAt > MODAL_COOLDOWN_MS) {
            setShowCreateAccountModal(true);
            setShowCGVModal(false);
            return;
          }
        }

        if (!currentUser.isAnonymous && uData.CGV !== 'accepted') {
          setAcceptedCGV(false);
          setShowCGVModal(true);
        } else {
          setShowCGVModal(false);
        }
      });
    });

    return () => {
      stopCompanyListeners();
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [diffSyncPhotos, discoverCompanyLink, isAuthLoaded, loadCachedPhotos, navigate, stopCompanyListeners]);

  const coerceTimestamp = useCallback((value) => {
    if (!value) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'object') {
      if (typeof value.toDate === 'function') {
        try { return value.toDate().getTime(); } catch {}
      }
      if (typeof value.seconds === 'number') {
        const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds / 1e6 : 0;
        return value.seconds * 1000 + nanos;
      }
      if (typeof value._seconds === 'number') {
        const nanos = typeof value._nanoseconds === 'number' ? value._nanoseconds / 1e6 : 0;
        return value._seconds * 1000 + nanos;
      }
    }
    return 0;
  }, []);

  const computeLastActivity = useCallback((data) => {
    if (!data || typeof data !== 'object') return 0;
    const timestamps = [];
    const push = (val) => {
      const ms = coerceTimestamp(val);
      if (ms) timestamps.push(ms);
    };
    const inspectEntry = (entry) => {
      if (!entry) return;
      if (typeof entry !== 'object') { push(entry); return; }
      push(entry);
      const keys = ['date', 'timestamp', 'createdAt', 'time', 'at', 'updatedAt', 'lastVisit', 'lastScan', 'lastActivity'];
      keys.forEach((key) => { if (entry[key]) push(entry[key]); });
    };

    const arrayFields = ['timestamps', 'resetstamp', 'pointshistory', 'transferHistory'];
    arrayFields.forEach((field) => {
      const arr = Array.isArray(data[field]) ? data[field] : [];
      arr.forEach(inspectEntry);
    });

    const directFields = ['updatedAt', 'lastVisit', 'lastScan', 'lastActivity', 'lastTimestamp'];
    directFields.forEach((key) => { if (data[key]) push(data[key]); });

    return timestamps.length ? Math.max(...timestamps) : 0;
  }, [coerceTimestamp]);

  const FIDELITY_INDEX_COLLECTION = 'fidelityIndex';
  const FIDELITY_INDEX_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 jours de validité du cache distant

  const loadRemoteFidIndex = useCallback(async (uid) => {
    if (!uid) return null;
    try {
      const ref = doc(firestore, FIDELITY_INDEX_COLLECTION, uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;

      const data = snap.data() || {};
      const merchantIds = uniqSorted(data.merchantIds || data.ids || data.merchants);
      const ts = coerceTimestamp(data.updatedAt || data.updatedOn || data.ts || data.lastSyncedAt);

      remoteIndexRef.current = ref;
      remoteIndexKeyRef.current = merchantIds.length ? merchantIds.join('|') : '';

      return { ref, merchantIds, ts };
    } catch (err) {
      console.warn('loadRemoteFidIndex error:', err);
      return null;
    }
  }, [coerceTimestamp]);

  const persistRemoteFidIndex = useCallback((uid, merchantIds) => {
    if (!uid) return;
    const normalized = uniqSorted(merchantIds);
    const key = normalized.join('|');

    // Pas d'écriture si rien ne change
    const alreadyPersisted =
      key === remoteIndexKeyRef.current &&
      key === lastPersistedIndexKeyRef.current &&
      !!remoteIndexRef.current;
    if (alreadyPersisted) return;
    lastPersistedIndexKeyRef.current = key;

    const write = async () => {
      try {
        const ref = remoteIndexRef.current || doc(firestore, FIDELITY_INDEX_COLLECTION, uid);
        remoteIndexRef.current = ref;
        await setDoc(
          ref,
          {
            merchantIds: normalized,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        remoteIndexKeyRef.current = key;
      } catch (err) {
        console.warn('persistRemoteFidIndex error:', err);
      }
    };

    if (indexPersistTimerRef.current) {
      clearTimeout(indexPersistTimerRef.current);
    }
    indexPersistTimerRef.current = setTimeout(() => {
      indexPersistTimerRef.current = null;
      write();
    }, 400);
  }, []);

  // Métadonnées marchand accessibles aux clients.
  const fetchMerchantMeta = useCallback(async (merchantId) => {
    const base = {
      Type: 'Fidbycount',
      multiple: 0,
      gift: '',
      points1: 0,
      pseudo: 'Inconnu',
    };
    if (!merchantId) return base;
    const pickPseudo = (data = {}) =>
      data.pseudo ||
      data.entreprise_name ||
      data.companyName ||
      data.displayName ||
      data.name ||
      null;

    let meta = { ...base };

    // 1) Doc fidélité : Type / multiple / gifts / points
    try {
      const fidSnap = await getDoc(doc(firestore, 'fidelity', merchantId));
      if (fidSnap.exists()) {
        const d = fidSnap.data() || {};
        meta = {
          ...meta,
          Type: d.Type || d.type || meta.Type,
          multiple: Number(d.multiple || 0) || meta.multiple,
          gift: d.gift || meta.gift,
          points1: Number(d.points1 || d.firstRewardPoints || 0) || meta.points1,
          pseudo: d.pseudo || meta.pseudo,
        };
      }
    } catch {
      // ignore
    }

    // 2) Compléter pseudo via entreprises si manquant
    if (!meta.pseudo || meta.pseudo === 'Inconnu') {
      try {
        const entSnap = await getDoc(doc(firestore, 'entreprises', merchantId));
        if (entSnap.exists()) {
          const d = entSnap.data() || {};
          meta.pseudo = pickPseudo(d) || meta.pseudo;
        } else {
          const qs = await getDocs(
            query(collection(firestore, 'entreprises'), where('userId', '==', merchantId), fsLimit(1))
          );
          if (!qs.empty) {
            meta.pseudo = pickPseudo(qs.docs[0].data() || {}) || meta.pseudo;
          }
        }
      } catch {
        // ignore
      }
    }

    return meta;
  }, []);

  const scheduleMetaHydration = useCallback((merchantId) => {
    if (merchantMetaCache.current[merchantId] || pendingMetaFetch.current.has(merchantId)) return;
    pendingMetaFetch.current.add(merchantId);
    fetchMerchantMeta(merchantId)
      .then((meta) => {
        merchantMetaCache.current[merchantId] = meta;
        const existing = cardsMap.current[merchantId];
        if (existing) {
          cardsMap.current[merchantId] = {
            ...existing,
            merchantPseudo: meta.pseudo || existing.merchantPseudo,
            Type: meta.Type || existing.Type,
            multiple: meta.multiple || existing.multiple,
            points1: meta.points1 || existing.points1,
          };
          commitCards();
        }
      })
      .finally(() => {
        pendingMetaFetch.current.delete(merchantId);
      });
  }, [commitCards, fetchMerchantMeta]);

  const upsertCard = useCallback((merchantId, clientData) => {
    if (!merchantId) return;
    if (!cardsMap.current[merchantId]) {
      registerPendingMerchant(merchantId);
    }

    const meta = merchantMetaCache.current[merchantId] || {};
    const fallbackType = clientData?.Type || cardsMap.current[merchantId]?.Type || 'Fidbycount';
    const fallbackMultiple = clientData?.multiple || cardsMap.current[merchantId]?.multiple || 0;
    const fallbackPoints1 = clientData?.points1 || cardsMap.current[merchantId]?.points1 || 0;
    const fallbackPseudo = cardsMap.current[merchantId]?.merchantPseudo || 'Inconnu';

    cardsMap.current[merchantId] = {
      merchantId,
      merchantPseudo: meta.pseudo || fallbackPseudo,
      Type: meta.Type || fallbackType,
      multiple: meta.multiple || fallbackMultiple,
      compteur: clientData?.compteur || 0,
      points: clientData?.points || 0,
      points1: meta.points1 || fallbackPoints1,
      lastActivity: computeLastActivity(clientData),
    };
    commitCards();
    resolvePendingMerchant(merchantId);
    scheduleMetaHydration(merchantId);
  }, [commitCards, computeLastActivity, registerPendingMerchant, resolvePendingMerchant, scheduleMetaHydration]);

  const detachMerchant = useCallback((merchantId) => {
    if (!merchantId) return;
    attachedMerchants.current.delete(merchantId);
    if (cardsMap.current[merchantId]) {
      delete cardsMap.current[merchantId];
      commitCards();
    }
  }, [commitCards]);

  const attachMerchantListeners = useCallback(async (merchantId, uid, options = {}) => {
    const { expectData = false, docRef = null, initialData = null } = options;
    if (!merchantId || !uid) return;

    const alreadyAttached = attachedMerchants.current.has(merchantId);
    if (alreadyAttached && !docRef) {
      if (initialData) {
        upsertCard(merchantId, initialData);
      }
      return;
    }

    attachedMerchants.current.add(merchantId);

    const teardownNewSchema = () => {
      if (newSchemaUnsubs.current[merchantId]) {
        newSchemaUnsubs.current[merchantId]();
        delete newSchemaUnsubs.current[merchantId];
      }
    };

    const teardownLegacy = () => {
      if (legacyUnsubs.current[merchantId]) {
        legacyUnsubs.current[merchantId]();
        delete legacyUnsubs.current[merchantId];
      }
    };

    const cleanupAll = () => {
      teardownNewSchema();
      teardownLegacy();
      detachMerchant(merchantId);
      resolvePendingMerchant(merchantId);
    };

    const ensureAttached = () => {
      if (!attachedMerchants.current.has(merchantId)) {
        attachedMerchants.current.add(merchantId);
      }
    };

    const startLegacyListener = () => {
      const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', uid);
      let seenInitial = false;
      legacyUnsubs.current[merchantId] = onSnapshot(
        legacyRef,
        (snapshot) => {
          seenInitial = true;
          if (snapshot.exists()) {
            ensureAttached();
            upsertCard(merchantId, snapshot.data() || {});
          } else {
            teardownLegacy();
            cleanupAll();
          }
        },
        (error) => {
          console.error('legacy listener error:', error);
          teardownLegacy();
          if (!seenInitial) {
            cleanupAll();
          }
        }
      );
    };

    if (docRef) {
      teardownNewSchema();
      ensureAttached();
      if (initialData) {
        upsertCard(merchantId, initialData);
      }
      newSchemaUnsubs.current[merchantId] = onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            ensureAttached();
            upsertCard(merchantId, snapshot.data() || {});
          } else {
            teardownNewSchema();
            startLegacyListener();
          }
        },
        (error) => {
          console.error('new schema listener error:', error);
          teardownNewSchema();
          startLegacyListener();
        }
      );
      return;
    }

    if (expectData) registerPendingMerchant(merchantId);

    const listRef = collection(firestore, 'fidelity', merchantId, 'list');
    const newQ = query(listRef, where('clientUid', '==', uid), fsLimit(1));

    newSchemaUnsubs.current[merchantId] = onSnapshot(
      newQ,
      (snap) => {
        if (!snap.empty) {
          ensureAttached();
          const docData = snap.docs[0].data() || {};
          upsertCard(merchantId, docData);
        } else {
          teardownNewSchema();
          startLegacyListener();
        }
      },
      (err) => {
        console.error('per-merchant new schema listener error:', err);
        teardownNewSchema();
        startLegacyListener();
      }
    );
  }, [detachMerchant, registerPendingMerchant, resolvePendingMerchant, upsertCard]);

  useEffect(() => {
    if (!isAuthLoaded) return;
    const auth = getAuth();

    let stopAuth = () => {};
    let cancelled = false;

    const start = onAuthStateChanged(auth, async (currentUser) => {
      Object.values(legacyUnsubs.current).forEach(fn => fn && fn());
      legacyUnsubs.current = {};
      Object.values(newSchemaUnsubs.current).forEach(fn => fn && fn());
      newSchemaUnsubs.current = {};
      cardsMap.current = {};
      attachedMerchants.current = new Set();
      resetInitialFidelityTracker();

      if (!currentUser) {
        if (currentUidRef.current) {
          clearFidelityCache(currentUidRef.current);
        }
        currentUidRef.current = null;
        remoteIndexRef.current = null;
        remoteIndexKeyRef.current = '';
        lastPersistedIndexKeyRef.current = '';
        if (indexPersistTimerRef.current) {
          clearTimeout(indexPersistTimerRef.current);
          indexPersistTimerRef.current = null;
        }
        setFidelityData([]);
        return;
      }

      const uid = currentUser.uid;
      currentUidRef.current = uid;
      remoteIndexRef.current = null;
      remoteIndexKeyRef.current = '';
      lastPersistedIndexKeyRef.current = '';
      if (indexPersistTimerRef.current) {
        clearTimeout(indexPersistTimerRef.current);
        indexPersistTimerRef.current = null;
      }

      const cachedCards = loadFidelityCache(uid);
      if (cachedCards.length) {
        const hydrated = {};
        cachedCards.forEach((card) => {
          if (card && typeof card === 'object' && card.merchantId) {
            hydrated[card.merchantId] = card;
          }
        });
        cardsMap.current = hydrated;
        setFidelityData(cachedCards);
      } else {
        setFidelityData([]);
      }

      const cacheKey = `fid_merchants_${uid}`;
      const cached = localStorage.getItem(cacheKey);
      let cachedIds = [];
      if (cached) {
        try { cachedIds = JSON.parse(cached); } catch {}
      }
      const remoteIndex = await loadRemoteFidIndex(uid);
      const remoteIds = remoteIndex?.merchantIds || [];
      const remoteTs = remoteIndex?.ts || 0;
      remoteIndexKeyRef.current = remoteIds.length ? remoteIds.join('|') : '';

      const unionIds = uniqSorted([...(Array.isArray(cachedIds) ? cachedIds : []), ...remoteIds]);
      const cacheSet = new Set(unionIds);

      const attachFromList = async (ids) => {
        if (!Array.isArray(ids) || !ids.length) return [];
        const unique = uniqSorted(ids);
        const tasks = unique.map((mid) => attachMerchantListeners(mid, uid, { expectData: true }));
        const results = await Promise.allSettled(tasks);
        const ok = [];
        results.forEach((res, idx) => {
          if (res.status === 'fulfilled') ok.push(unique[idx]);
        });
        return ok;
      };

      await attachFromList(unionIds);

      const indexFresh =
        remoteTs > 0 &&
        Date.now() - remoteTs < FIDELITY_INDEX_TTL_MS;

      if (indexFresh) {
        if (!cancelled) {
          localStorage.setItem(cacheKey, JSON.stringify(unionIds));
          if (unionIds.join('|') !== remoteIndexKeyRef.current) {
            persistRemoteFidIndex(uid, unionIds);
          }
        }
        completeInitialFidelityScan();
        return;
      }

      const runNewSchemaScan = async () => {
        const cgQuery = query(
          collectionGroup(firestore, 'list'),
          where('clientUid', '==', uid),
          fsLimit(LEGACY_SCAN_LIMIT)
        );
        const cgSnap = await getDocs(cgQuery);
        const foundNewSchema = [];
        const attachTasks = [];
        for (const docSnap of cgSnap.docs) {
          const merchantId = docSnap.ref?.parent?.parent?.id;
          if (!merchantId || attachedMerchants.current.has(merchantId)) continue;
          foundNewSchema.push(merchantId);
          attachTasks.push(
            attachMerchantListeners(merchantId, uid, {
              expectData: true,
              docRef: docSnap.ref,
              initialData: docSnap.data() || {},
            })
          );
        }
        if (attachTasks.length) {
          await Promise.allSettled(attachTasks);
        }
        return foundNewSchema;
      };

      const runLegacyScan = async () => {
        const merchantsSnap = await getDocs(query(collection(firestore, 'fidelity'), fsLimit(LEGACY_SCAN_LIMIT)));
        const tasks = merchantsSnap.docs.map(async (m) => {
          const merchantId = m.id;
          if (attachedMerchants.current.has(merchantId)) return null;

          const listRef = collection(firestore, 'fidelity', merchantId, 'list');
          const testNew = await getDocs(query(listRef, where('clientUid', '==', uid), fsLimit(1)));
          if (!testNew.empty) {
            await attachMerchantListeners(merchantId, uid, { expectData: true });
            return merchantId;
          }

          const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', uid);
          const legacySnap = await getDoc(legacyRef);
          if (legacySnap.exists()) {
            await attachMerchantListeners(merchantId, uid, { expectData: true });
            return merchantId;
          }
          return null;
        });

        if (!tasks.length) return [];

        const results = await Promise.allSettled(tasks);
        const foundLegacy = [];
        results.forEach((res) => {
          if (res.status === 'fulfilled' && res.value) {
            foundLegacy.push(res.value);
          }
        });
        return foundLegacy;
      };

      const [newResult, legacyResult] = await Promise.allSettled([
        runNewSchemaScan(),
        runLegacyScan(),
      ]);

      const newIds = newResult.status === 'fulfilled' ? newResult.value : [];
      const legacyIds = legacyResult.status === 'fulfilled' ? legacyResult.value : [];

      if (newResult.status === 'rejected') {
        console.error('collectionGroup scan error:', newResult.reason);
      }

      if (legacyResult.status === 'rejected') {
        const label = newIds.length ? 'bounded scan (fallback) error:' : 'bounded scan error:';
        console.error(label, legacyResult.reason);
      }

      if (!cancelled) {
        if (newIds.length) newIds.forEach((id) => cacheSet.add(id));
        if (legacyIds.length) legacyIds.forEach((id) => cacheSet.add(id));
        const merged = uniqSorted(Array.from(cacheSet));
        const original = uniqSorted(Array.isArray(cachedIds) ? cachedIds : []);

        if (merged.join('|') !== original.join('|')) {
          localStorage.setItem(cacheKey, JSON.stringify(merged));
        }
        if (merged.join('|') !== remoteIndexKeyRef.current) {
          persistRemoteFidIndex(uid, merged);
        }
      }
      completeInitialFidelityScan();
    });

    stopAuth = () => start();
    return () => {
      cancelled = true;
      Object.values(legacyUnsubs.current).forEach(fn => fn && fn());
      legacyUnsubs.current = {};
      Object.values(newSchemaUnsubs.current).forEach(fn => fn && fn());
      newSchemaUnsubs.current = {};
      if (indexPersistTimerRef.current) {
        clearTimeout(indexPersistTimerRef.current);
        indexPersistTimerRef.current = null;
      }
      stopAuth();
    };
  }, [attachMerchantListeners, clearFidelityCache, completeInitialFidelityScan, isAuthLoaded, loadFidelityCache, loadRemoteFidIndex, persistRemoteFidIndex, registerPendingMerchant, resetInitialFidelityTracker, FIDELITY_INDEX_TTL_MS]);

  const handleCreateAccount = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.isAnonymous) return;
    if (isCreateDisabled) return;

    if (!PASSWORD_REGEX.test(newPassword || '')) {
      alert(PASSWORD_POLICY.message);
      return;
    }

    setIsCreating(true);

    const sanitizedEmail = sanitize(newEmail);
    const sanitizedPassword = sanitize(newPassword);
    const sanitizedAccountPseudo = sanitize(newAccountPseudo);

    const credential = EmailAuthProvider.credential(sanitizedEmail, sanitizedPassword);
    try {
      const linkedUser = await linkWithCredential(currentUser, credential);
      const userRef = doc(firestore, 'users', linkedUser.user.uid);
      await setDoc(
        userRef,
        {
          email: sanitizedEmail,
          pseudo: sanitizedAccountPseudo,
          createdAt: serverTimestamp(),
          role: 'client',
          CGV: 'accepted'
        },
        { merge: true }
      );
      // Poser needsEmailVerification: true + déclencher la gate de vérification email
      try {
        const idToken = await linkedUser.user.getIdToken();
        const apiBase = ((process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')) ||
          (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
        await fetch(`${apiBase}/api/auth/flag-verification`, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        await reloadEmailVerified(true);
      } catch (flagErr) {
        console.warn('flag-verification non critique :', flagErr);
      }

      localStorage.setItem(MODAL_KEY, Date.now().toString());
      setShowCreateAccountModal(false);
      setShowCGVModal(false);
      setUser((prev) => ({
        ...prev,
        CGV: 'accepted',
        pseudo: sanitizedAccountPseudo,
        isAnonymous: false
      }));
    } catch (error) {
      console.error('Erreur création compte', error);
      setIsCreating(false);
      if (error.code === 'auth/email-already-in-use') {
        alert('Email déjà utilisé.');
      } else {
        alert('Erreur : ' + error.message);
      }
    }
  };

  const handleCloseCreateModal = () => {
    localStorage.setItem(MODAL_KEY, Date.now().toString());
    setShowCreateAccountModal(false);
    if (!hasAcceptedCGU) {
      setAcceptedCGV(false);
      setShowCGVModal(true);
    }
  };

  const handleSaveCGV = async () => {
    const auth = getAuth();
    const cu = auth.currentUser;
    if (!cu) return;

    if (isCGVButtonDisabled) {
      alert('Veuillez accepter les CGU.');
      return;
    }
    const uref = doc(firestore, 'users', cu.uid);
    await updateDoc(uref, { CGV: 'accepted' });
    setShowCGVModal(false);
    setUser((prev) => ({ ...prev, CGV: 'accepted' }));
  };

  const handleBackToInvoices = () => navigate('/clientinvoices');
  const handleGoFidelity = () => {
    if (!isFidelityReady) return;
    navigate('/clientfid');
  };
  const handleOpenMerchant = useCallback(
    (merchantId) => {
      if (!merchantId) return;
      navigate(`/clientfidviewer/${merchantId}`);
    },
    [navigate]
  );

  const isRewardImminent = useCallback((item) => {
    if (!item) return false;
    if (item.Type === 'Fidbycount') {
      const multiple = Number(item.multiple || 0);
      const compteur = Number(item.compteur || 0);
      return multiple > 0 && multiple - compteur === 1;
    }
    if (item.Type === 'Fidbypoints') {
      const points = Number(item.points || 0);
      const threshold = Number(item.points1 || 0);
      return threshold > 0 && points >= threshold;
    }
    return false;
  }, []);

  const getCardText = (item) => {
    if (item.Type === 'Fidbycount') {
      const isEligible = isRewardImminent(item);
      return (
        <>
          <p style={{ margin: 0 }}>
            {item.compteur} / {item.multiple} passages
          </p>
          {isEligible && (
            <p className="client-docs-imminentText">
              Gain au prochain passage !
            </p>
          )}
        </>
      );
    }
    if (item.Type === 'Fidbypoints') {
      const isEligible = isRewardImminent(item);
      return (
        <>
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--eb-text-2)' }}>
            {item.points} points
          </p>
          {isEligible && (
            <p className="client-docs-imminentText">
              Gain disponible !
            </p>
          )}
        </>
      );
    }
    return null;
  };

  const handleViewPhoto = useCallback(
    (photo) => {
      if (!photo) return;
      navigate('/clientphotoviewer', { state: { photo } });
    },
    [navigate]
  );

  const formatPhotoTitle = (title) => {
    if (!title) return 'Sans titre';
    const trimmed = String(title).trim();
    if (trimmed.length > 18) return `${trimmed.slice(0, 18)}…`;
    return trimmed || 'Sans titre';
  };

  const pluralizeLabel = (count, singular, plural) => (count === 1 ? singular : plural);
  const greetingName = user?.pseudo || user?.firstName || user?.prenom || '';
  const heroGreeting = greetingName ? `Bonjour ${greetingName}` : 'Bonjour';
  const currentUserId = typeof user?.uid === 'string' ? user.uid : '';
  const getPhotoAuthorLabel = useCallback(
    (photo) => {
      if (!photo || typeof photo !== 'object') return 'Document importé';
      const uploaderUid = typeof photo.takenByUid === 'string' ? photo.takenByUid : '';
      const uploaderPseudo = typeof photo.takenByPseudo === 'string' ? photo.takenByPseudo : '';
      if (currentUserId && uploaderUid && uploaderUid === currentUserId) {
        return 'Par vous';
      }
      if (uploaderPseudo) {
        return `Par ${uploaderPseudo}`;
      }
      return 'Document importé';
    },
    [currentUserId]
  );

  const invoiceCount = isCompanyLinked ? photos.length : 0;
  const loyaltyCount = fidelityData.length;
  const totalPoints = fidelityData.reduce(
    (sum, item) => sum + (item.Type === 'Fidbypoints' ? item.points || 0 : 0),
    0
  );
  const upcomingRewards = fidelityData.reduce(
    (sum, item) => sum + (isRewardImminent(item) ? 1 : 0),
    0
  );

  const invoicesCardClickable = false;
  const fidelityCardClickable = false;

  const sortedLoyaltyData = isFidelityReady ? sortLoyaltyData(fidelityData, isRewardImminent) : [];
  const loyaltyPreview = sortedLoyaltyData.slice(0, 3);
  const triangleLayout = loyaltyPreview.length === 3;

  const handleInvoicesKeyDown = (event) => {
    if (!invoicesCardClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBackToInvoices();
    }
  };

  const handleFidelityKeyDown = (event) => {
    if (!fidelityCardClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleGoFidelity();
    }
  };

  if (!isAuthLoaded) return null;

  return (
    <>
      <style>{pageStyles}</style>
      <div className="dashboard-container client-docs-dashboard">
        <ClientNavbar />

        {showCreateAccountModal && (
          <div className="client-docs-overlay">
            <div className="client-docs-modal eb-fade-up" role="dialog" aria-modal="true">
              <button
                type="button"
                onClick={handleCloseCreateModal}
                className="client-docs-modal__close"
                aria-label="Fermer"
              >
                &times;
              </button>
              <h2>Créer mon compte</h2>
              <input
                type="email"
                className="eb-input"
                value={newEmail}
                onChange={(e) => e.target && setNewEmail(e.target.value)}
                placeholder="Email"
                aria-label="Email"
                inputMode="email"
                autoComplete="email"
              />

              <div className="client-docs-pwd">
                <PasswordField
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mot de passe"
                  className="eb-input"
                  inputStyle={newPassword && !isPwdValid ? { borderColor: 'var(--eb-danger)' } : undefined}
                  autoComplete="new-password"
                  pattern={PASSWORD_REGEX.source}
                  title={PASSWORD_POLICY.message}
                  aria-label="Mot de passe"
                  aria-invalid={!!newPassword && !isPwdValid}
                />
              </div>

              <input
                type="text"
                className="eb-input"
                value={newAccountPseudo}
                onChange={(e) => setNewAccountPseudo(e.target.value)}
                placeholder="Pseudo"
                aria-label="Pseudo"
                autoComplete="nickname"
              />

              {!hasAcceptedCGU && (
                <div>
                  <LegalAccordion docId="cgu" checked={acceptedCGVCreate} onChange={setAcceptedCGVCreate} />
                </div>
              )}

              <button
                type="button"
                onClick={handleCreateAccount}
                disabled={isCreateDisabled}
                className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
              >
                {isCreating ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
                {isCreating ? 'Création...' : 'Créer mon compte'}
              </button>
            </div>
          </div>
        )}

        {showCGVModal && !showCreateAccountModal && (
          <div className="client-docs-overlay">
            <div className="client-docs-modal eb-fade-up" role="dialog" aria-modal="true">
              <h2 style={{ paddingRight: 0 }}>Conditions d'utilisation</h2>
              <div>
                <LegalAccordion docId="cgu" checked={acceptedCGV} onChange={setAcceptedCGV} />
              </div>
              <button
                type="button"
                onClick={handleSaveCGV}
                disabled={isCGVButtonDisabled}
                className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
              >
                Accepter
              </button>
            </div>
          </div>
        )}

        <div className="client-docs-shell">
          <div className="client-docs-inner">
            <section className="client-docs-hero">
              <div className="client-docs-heroGrid">
                <div className="client-docs-heroIntro">
                  <h1>{heroGreeting}</h1>
                  <p>Gérez vos programmes de fidélité et vos documents en un seul endroit</p>
                </div>
                <div className="client-docs-heroStats">
                  <div className="client-docs-heroStat">
                    <span className="client-docs-statValue">{invoiceCount}</span>
                    <span className="client-docs-statLabel">
                      {pluralizeLabel(invoiceCount, 'Document', 'Documents')}
                    </span>
                  </div>
                  <div className="client-docs-heroStat">
                    <span className="client-docs-statValue">{loyaltyCount}</span>
                    <span className="client-docs-statLabel">
                      {pluralizeLabel(loyaltyCount, 'Programme actif', 'Programmes actifs')}
                    </span>
                  </div>
                  {upcomingRewards > 0 ? (
                    <div className="client-docs-heroStat">
                      <span className="client-docs-statValue">{upcomingRewards}</span>
                      <span className="client-docs-statLabel">
                        {pluralizeLabel(upcomingRewards, 'Récompense imminente', 'Récompenses imminentes')}
                      </span>
                    </div>
                  ) : totalPoints > 0 ? (
                    <div className="client-docs-heroStat">
                      <span className="client-docs-statValue">{totalPoints}</span>
                      <span className="client-docs-statLabel">Points cumulés</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="client-docs-stage">
              <article
                className={`client-docs-panel ${fidelityCardClickable ? 'client-docs-panel--clickable' : ''}`}
                onClick={fidelityCardClickable ? handleGoFidelity : undefined}
                onKeyDown={handleFidelityKeyDown}
                role={fidelityCardClickable ? 'button' : 'region'}
                tabIndex={fidelityCardClickable ? 0 : undefined}
                aria-label="Accéder à votre fidélité"
              >
                <header className="client-docs-panelHeader">
                  <div className="client-docs-panelTitle">
                    <span>Programmes</span>
                    <h2>Fidélité</h2>
                  </div>
                  <button
  type="button"
  className="client-docs-actionPill"
  onClick={(event) => {
    event.stopPropagation();
    navigate('/transfertfid');
  }}
  aria-label="Partagez votre fidélité"
  title="Partagez votre fidélité"
>
  {/* Mets exactement le texte que tu veux ici */}
  Partagez votre fidélité
  {/* ou "Partagez-votre fidélité" si tu tiens au tiret */}
</button>

                </header>

                {!isFidelityReady ? (
                  <div className="client-docs-panelBody">
                    <div className="client-docs-loyaltyLoading"><span className="eb-spinner eb-spinner--sm" aria-hidden="true" />Chargement de vos programmes…</div>
                  </div>
                ) : loyaltyCount > 0 ? (
                  <div className="client-docs-panelBody client-docs-panelBody--rail">
                    <div className={`client-docs-railTrack ${triangleLayout ? 'client-docs-railTrack--triangle' : ''}`}>
                      {loyaltyPreview.map((item, index) => (
                        <button
                          key={`${item.merchantId}-${index}`}
                          type="button"
                          className={`client-docs-loyaltyCard ${triangleLayout ? (index === 2 ? 'client-docs-loyaltyCard--triangleBottom' : 'client-docs-loyaltyCard--triangleTop') : ''}${isRewardImminent(item) ? ' client-docs-loyaltyCard--imminent' : ''}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenMerchant(item.merchantId);
                          }}
                          aria-label={item.merchantPseudo ? `Voir ${item.merchantPseudo}` : 'Voir le programme'}
                          title={item.merchantPseudo || 'Programme fidélité'}
                        >
                          <div className="client-docs-loyaltyAvatar" data-initial={(item.merchantPseudo || '?').trim().charAt(0)}>
                            <MerchantLogo merchantId={item.merchantId} />
                          </div>
                          <div className="client-docs-loyaltyInfo">
                            <h3>{item.merchantPseudo}</h3>
                            <div className="client-docs-loyaltyMeta">{getCardText(item)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="client-docs-panelBody">
                    <div className="client-docs-empty">
                      Scannez chez vos commerçants pour commencer à accumuler des points
                    </div>
                  </div>
                )}

                <footer className="client-docs-panelFooter">
                  <button
                    type="button"
                    className="client-docs-cta client-docs-cta--ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleGoFidelity();
                    }}
                    disabled={!isFidelityReady || loyaltyCount === 0}
                  >
                    Voir tous les programmes
                  </button>
                </footer>
              </article>

              {isCompanyLinked && (
                <article
                  className={`client-docs-panel ${invoicesCardClickable ? 'client-docs-panel--clickable' : ''}`}
                  onClick={invoicesCardClickable ? handleBackToInvoices : undefined}
                  onKeyDown={handleInvoicesKeyDown}
                  role={invoicesCardClickable ? 'button' : 'region'}
                  tabIndex={invoicesCardClickable ? 0 : undefined}
                  aria-label="Accéder à vos documents"
                >
                  <header className="client-docs-panelHeader">
                    <div className="client-docs-panelTitle">
                      <span>Documents</span>
                      <h2>Factures</h2>
                    </div>
                    <button
                      type="button"
                      className="client-docs-actionPill"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleImportClick();
                      }}
                    >
                      <FaUpload aria-hidden="true" />
                      <span>Importer</span>
                    </button>
                  </header>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImportInvoice}
                  />

                  {invoiceCount > 0 ? (
                    <div className="client-docs-panelBody client-docs-panelBody--stack">
                      <div className="client-docs-invoiceList">
                        {photos.slice(0, 3).map((photo, idx) => (
                          <button
                            key={`${photo.name || 'photo'}-${photo.timestamp || idx}`}
                            type="button"
                            className="client-docs-invoiceRow"
                            title={photo.title || 'Sans titre'}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewPhoto(photo);
                            }}
                          >
                            <div className="client-docs-invoicePreview">
                              <img loading="lazy" src={photo.url} alt={photo.title || 'Document'} />
                            </div>
                            <div className="client-docs-invoiceCopy">
                              <span className="client-docs-invoiceTitle">
                                {formatPhotoTitle(photo.title)}
                              </span>
                              <span className="client-docs-invoiceSubtitle">
                                {getPhotoAuthorLabel(photo)}
                              </span>
                            </div>
                            <span className="client-docs-invoiceChevron" aria-hidden="true">›</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="client-docs-panelBody">
                      <div className="client-docs-empty">
                        Réalisez votre premier scan pour enregistrer vos documents
                      </div>
                    </div>
                  )}

                  <footer className="client-docs-panelFooter">
                    <button
                      type="button"
                      className="client-docs-cta client-docs-cta--ghost"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleBackToInvoices();
                      }}
                    >
                      Voir tous les documents
                    </button>
                  </footer>
                </article>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientDocs;
